"use client";

import React, { useMemo, useState } from "react";
import Modal from "@/components/modal";
import axiosInstance from "@/utils/axiosInstance";
import { formatCurrency } from "@/utils/formatCurrency";
import { toast } from "react-toastify";

const onlyDigits = (value: string) => value.replace(/\D+/g, "");

type StepState = "form" | "done";
type PixKeyType = "CPF" | "CNPJ" | "EMAIL" | "PHONE" | "EVP";

type PixRegistration = {
  key: string | null | undefined;
  type: string | null | undefined;
  status?: string | null | undefined;
  approvalStatus?: string | null | undefined;
};

type SubscriptionWithdrawModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
  currency?: string | null;
  onSuccess?: () => Promise<void> | void;
  pixRegistration?: PixRegistration;
  onRequestPixSetup?: () => void;
};

const normalizePixKeyType = (value?: string | null): PixKeyType => {
  const normalized = (value ?? "").toLowerCase();
  switch (normalized) {
    case "cpf":
      return "CPF";
    case "cnpj":
      return "CNPJ";
    case "email":
      return "EMAIL";
    case "phone":
      return "PHONE";
    default:
      return "EVP";
  }
};

const resolvePixTypeLabel = (value?: string | null) => {
  const normalized = (value ?? "").toLowerCase();
  switch (normalized) {
    case "cpf":
      return "CPF";
    case "cnpj":
      return "CNPJ";
    case "email":
      return "E-mail";
    case "phone":
      return "Telefone";
    case "random":
      return "Chave aleatória";
    default:
      return "Outro";
  }
};

const resolvePixStatusBadge = (status?: string | null) => {
  if (!status) {
    return null;
  }

  const normalized = status.toLowerCase();

  if (normalized === "approved") {
    return { label: "Aprovada", tone: "bg-green-100 text-green-800" };
  }

  if (
    normalized === "pending_review" ||
    normalized === "pending" ||
    normalized === "pending_approval"
  ) {
    return { label: "Em análise", tone: "bg-amber-100 text-amber-800" };
  }

  if (normalized === "rejected" || normalized === "denied") {
    return { label: "Rejeitada", tone: "bg-red-100 text-red-700" };
  }

  return { label: status, tone: "bg-slate-100 text-slate-700" };
};

const SubscriptionWithdrawModal: React.FC<SubscriptionWithdrawModalProps> = ({
  open,
  onOpenChange,
  availableBalance,
  currency,
  onSuccess,
  pixRegistration,
  onRequestPixSetup,
}) => {
  const [step, setStep] = useState<StepState>("form");
  const [amount, setAmount] = useState<string>("");
  const [sending, setSending] = useState(false);

  const resetState = () => {
    setStep("form");
    setAmount("");
    setSending(false);
  };

  const closeModal = () => {
    resetState();
    onOpenChange(false);
  };

  const formattedBalance = useMemo(
    () => formatCurrency(Number(availableBalance || 0), currency ?? "BRL"),
    [availableBalance, currency],
  );

  const rawPixKey = pixRegistration?.key?.trim() ?? "";
  const pixKeyType = normalizePixKeyType(pixRegistration?.type);
  const pixKeyLabel = resolvePixTypeLabel(pixRegistration?.type);
  const pixKeyStatus = (pixRegistration?.status ?? "").toLowerCase();
  const approvalStatus = (pixRegistration?.approvalStatus ?? "").toLowerCase();

  const hasPixKey = rawPixKey.length > 0;
  const isPendingApproval =
    pixKeyStatus === "pending_review" || approvalStatus === "pending_approval";
  const isRejected =
    pixKeyStatus === "rejected" ||
    pixKeyStatus === "denied" ||
    approvalStatus === "rejected";
  const allowWithdraw = hasPixKey && !isPendingApproval && !isRejected;
  const isApproved = allowWithdraw;
  const pixStatusBadge = resolvePixStatusBadge(
    pixRegistration?.status ?? pixRegistration?.approvalStatus ?? null,
  );

  const numericAmount = Number((amount || "0").toString().replace(",", "."));
  const maxAmount = Number(availableBalance || 0);
  const canSubmitForm =
    numericAmount > 0 && numericAmount <= maxAmount && allowWithdraw;

  const handleSubmitWithdrawal = async () => {
    if (!canSubmitForm) {
      return;
    }

    try {
      setSending(true);
      const keyForTransfer =
        pixKeyType === "PHONE" ? onlyDigits(rawPixKey) : rawPixKey;

      await axiosInstance.post("/subscriptions/wallet/withdrawals/request", {
        transfer_type: "PIX",
        amount: numericAmount,
        key_type: pixKeyType,
        key: keyForTransfer,
      });

      toast.success("Saque solicitado com sucesso");
      setStep("done");
      if (onSuccess) {
        await onSuccess();
      }
      setTimeout(() => closeModal(), 1200);
    } catch (error: any) {
      const message = error?.response?.data ?? error?.message;
      if (
        typeof message === "string" &&
        message.toLowerCase().includes("subconta asaas")
      ) {
        toast.error("Subconta Asaas não configurada. Configure no admin.");
      } else {
        toast.error(
          typeof message === "string" ? message : "Erro ao solicitar saque",
        );
      }
    } finally {
      setSending(false);
    }
  };

  const handleModalChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
    } else {
      closeModal();
    }
  };

  const renderPixStatusInfo = () => {
    if (!hasPixKey) {
      return (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-medium">Nenhuma chave PIX cadastrada.</p>
          <p className="mt-1">
            Cadastre uma chave PIX para habilitar as solicitações de saque.
          </p>
          {onRequestPixSetup && (
            <button
              type="button"
              onClick={onRequestPixSetup}
              className="mt-3 inline-flex items-center rounded bg-amber-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-amber-700"
            >
              Cadastrar chave PIX
            </button>
          )}
        </div>
      );
    }

    if (isPendingApproval) {
      return (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-medium">Chave PIX em análise.</p>
          <p className="mt-1">
            Aguarde a aprovação da chave cadastrada para liberar novas
            solicitações de saque.
          </p>
          <p className="mt-1">
            Precisa agilizar? Solicite ao suporte a aprovação da sua chave PIX
            pelos números (19) 99786-1291 ou (11) 91845-6133.
          </p>
        </div>
      );
    }

    if (isRejected) {
      return (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="font-medium">Chave PIX rejeitada.</p>
          <p className="mt-1">
            Atualize a chave cadastrada para continuar solicitando saques.
          </p>
          {onRequestPixSetup && (
            <button
              type="button"
              onClick={onRequestPixSetup}
              className="mt-3 inline-flex items-center rounded bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              Atualizar chave PIX
            </button>
          )}
        </div>
      );
    }

    if (isApproved && pixStatusBadge) {
      return (
        <div className="flex flex-wrap items-center gap-2 text-sm text-emerald-700">
          <span className="font-medium">Chave PIX aprovada para saques.</span>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${pixStatusBadge.tone}`}
          >
            {pixStatusBadge.label}
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleModalChange}
      titleModal="Solicitar Saque"
      size="default"
    >
      {step === "form" && (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Saldo disponível:{" "}
            <span className="font-medium">{formattedBalance}</span>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase text-gray-500">
                Chave PIX cadastrada
              </span>
              {hasPixKey ? (
                <>
                  <span className="text-base font-semibold text-gray-900">
                    {rawPixKey}
                  </span>
                  <span className="text-xs text-gray-500">
                    Tipo: {pixKeyLabel}
                  </span>
                </>
              ) : (
                <span className="text-xs text-gray-500">
                  Nenhuma chave cadastrada até o momento.
                </span>
              )}
            </div>
          </div>

          {renderPixStatusInfo()}

          <div className="flex flex-col gap-2">
            <label className="text-sm">Valor</label>
            <input
              type="number"
              min={1}
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="rounded border px-3 py-2 text-sm"
              placeholder="Ex: 100.00"
            />
            <small className="text-xs text-gray-500">
              O valor deve ser menor ou igual ao saldo disponível.
            </small>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              className="rounded border px-3 py-2 text-sm"
              onClick={closeModal}
              type="button"
            >
              Cancelar
            </button>
            <button
              disabled={!canSubmitForm || sending}
              className="rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSubmitWithdrawal}
              type="button"
            >
              {sending ? "Solicitando..." : "Solicitar saque"}
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="space-y-4">
          <div className="text-emerald-700">Saque solicitado com sucesso.</div>
          <div className="flex justify-end">
            <button
              className="rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white"
              onClick={closeModal}
              type="button"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SubscriptionWithdrawModal;
